"use client";

import { useEffect, useState } from "react";
import {
  getInAppBrowserInfo,
  buildAndroidChromeIntent,
  inAppBrowserLabel,
  type InAppBrowserInfo,
} from "@/shared/lib/in-app-browser";

/**
 * Full-screen block shown when Hoku is opened inside a social app's in-app
 * browser (Instagram, Facebook, TikTok, …). Those webviews break Google
 * login, camera capture, web push and PWA install, so we stop the user here
 * and route them to their real browser.
 *
 * - Android: an "Open in Chrome" button (intent:// escape hatch).
 * - iOS: webviews can't be escaped programmatically, so we show the
 *   "tap ⋯ → Open in browser" instruction.
 * - Both: a "Copy link" fallback.
 *
 * Renders nothing on the server and in normal standalone browsers.
 */
export default function InAppBrowserGate() {
  const [info, setInfo] = useState<InAppBrowserInfo | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setInfo(getInAppBrowserInfo());
  }, []);

  if (!info?.isInApp) return null;

  const appName = inAppBrowserLabel(info.app);
  const currentUrl =
    typeof window !== "undefined" ? window.location.href : "https://www.heyhoku.ai";

  const handleOpenChrome = () => {
    window.location.href = buildAndroidChromeIntent(currentUrl);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be blocked in webviews — fall back to a prompt so
      // the user can still copy manually.
      window.prompt("Copy this link and paste it into your browser:", currentUrl);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Open in your browser"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-cream px-7 py-10 text-center text-black"
    >
      <img src="/logo.png" alt="Hoku" className="h-14" />

      <div className="max-w-[340px] space-y-3">
        <h1 className="font-winky text-2xl font-bold text-charcoal">
          Open in your browser for the full experience
        </h1>
        <p className="font-winky text-sm leading-relaxed text-charcoal/70">
          {appName}&rsquo;s in-app browser blocks sign-in, camera and
          notifications. Open Hoku in Chrome or Safari to continue.
        </p>
      </div>

      <div className="flex w-full max-w-[320px] flex-col gap-3">
        {info.os === "android" && (
          <button
            onClick={handleOpenChrome}
            className="w-full rounded-full px-6 py-3 font-winky text-lg font-medium text-black transition-transform active:scale-95"
            style={{
              border: "2px solid #ffd372",
              backgroundColor: "#ffd372",
              boxShadow: "0 5px 0 #d4991c, 0 6px 15px rgba(76,76,76,0.3)",
            }}
          >
            Open in Chrome
          </button>
        )}

        <button
          onClick={handleCopy}
          className="w-full rounded-full bg-white px-6 py-3 font-winky text-lg font-medium text-black transition-transform active:scale-95"
          style={{ border: "2px solid #ffd372" }}
        >
          {copied ? "Link copied ✓" : "Copy link"}
        </button>
      </div>

      {info.os === "ios" && (
        <div className="max-w-[320px] rounded-2xl bg-white/70 px-5 py-4 font-winky text-sm text-charcoal/80">
          <p className="font-semibold text-charcoal">On iPhone:</p>
          <p className="mt-1">
            Tap the{" "}
            <span className="font-semibold">&middot;&middot;&middot;</span> (or
            share) icon in the corner, then choose{" "}
            <span className="font-semibold">&ldquo;Open in browser&rdquo;</span>.
          </p>
        </div>
      )}
    </div>
  );
}
